import React from "react";
import { Card, CardContent } from "../../../components/card";

interface MetricCardProps {
  label: string;
  value?: string | number;
  icon?: React.ReactNode;
  badge?: {
    text: string;
    variant: "success" | "error" | "warning";
  };
  footer?: {
    text: string;
    variant?: "success" | "error" | "warning";
  };
  description?: string;
  valueColor?: "default" | "orange" | "green";
  customHeight?: string;
  breakdown?: {
    in_app: number;
    in_store: number;
  };
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  icon,
  badge,
  footer,
  description,
  valueColor = "default",
  customHeight,
  breakdown,
}) => {
  const badgeColors = {
    success: "bg-[#f0fdf4] dark:bg-emerald-900/30 text-[#008236] dark:text-emerald-400",
    error: "bg-[#fef2f2] dark:bg-red-900/30 text-[#c10007] dark:text-red-400",
    warning: "bg-[#fefce8] dark:bg-yellow-900/30 text-[#ca8a04] dark:text-yellow-400",
  };

  const footerColors = {
    success: "text-[#008236] dark:text-emerald-400",
    error: "text-[#c10007] dark:text-red-400",
    warning: "text-[#ca8a04] dark:text-yellow-400",
  };

  const valueColorClasses = {
    default: "text-[#004232] dark:text-emerald-400",
    orange: "text-[#ea580c] dark:text-orange-500",
    green: "text-[#004232] dark:text-emerald-400",
  };

  return (
    <Card
      className={`bg-white dark:bg-gray-800 outline outline-1 outline-offset-[-1px] outline-neutral-200 dark:outline-gray-700 rounded-lg ${customHeight || "h-full"} flex flex-col`}
    >
      <CardContent className="p-3.5 flex flex-col flex-1">
        <div className="flex gap-4 items-start flex-1">
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <div
              className="text-slate-500 dark:text-gray-400 text-sm font-normal leading-5 whitespace-nowrap"
              style={{ fontFamily: "Segoe UI" }}
            >
              {label}
            </div>
            {breakdown ? (
              <div className="flex gap-0 items-start mt-0.5">
                <div className="flex-1 border-r border-neutral-200 dark:border-gray-600 flex flex-col justify-center items-center gap-[3px]">
                  <div
                    className="text-gray-700 dark:text-white text-xl font-normal leading-7 whitespace-nowrap"
                    style={{ fontFamily: "Cal Sans" }}
                  >
                    {breakdown.in_app}
                  </div>
                  <div
                    className="text-gray-600 dark:text-gray-400 text-xs font-normal leading-3 whitespace-nowrap"
                    style={{ fontFamily: "Nunito" }}
                  >
                    In-App
                  </div>
                </div>
                <div className="flex-1 flex flex-col justify-center items-center gap-[3px]">
                  <div
                    className="text-gray-700 dark:text-white text-xl font-normal leading-7 whitespace-nowrap"
                    style={{ fontFamily: "Cal Sans" }}
                  >
                    {breakdown.in_store}
                  </div>
                  <div
                    className="text-gray-600 dark:text-gray-400 text-xs font-normal leading-3 whitespace-nowrap"
                    style={{ fontFamily: "Nunito" }}
                  >
                    In-store
                  </div>
                </div>
              </div>
            ) : (
              value !== undefined && (
                <div
                  className={`text-[#004232] dark:text-emerald-400 text-xl font-normal leading-7 whitespace-nowrap truncate min-h-[28px] flex items-center`}
                  style={{ fontFamily: "Cal Sans" }}
                >
                  {value}
                </div>
              )
            )}
            {description && (
              <div
                className="text-gray-500 dark:text-gray-400 text-xs font-normal leading-4 whitespace-nowrap"
                style={{ fontFamily: "Nunito" }}
              >
                {description}
              </div>
            )}
          </div>
          {icon && <div className="w-5 h-5 relative shrink-0 flex-shrink-0">{icon}</div>}
        </div>
        {(badge || footer) && (
          <div className="flex gap-2 items-center flex-wrap min-h-0">
            {badge && (
              <div
                className={`px-2 py-1 rounded-lg text-xs font-normal leading-4 whitespace-nowrap shrink-0 ${
                  badge.variant === "error"
                    ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                    : badgeColors[badge.variant]
                }`}
                style={{ fontFamily: "Segoe UI" }}
              >
                {badge.text}
              </div>
            )}
            {footer &&
              (footer.variant === "success" ? (
                <div
                  className="px-2 py-1 bg-green-50 dark:bg-emerald-900/30 rounded-lg text-green-700 dark:text-emerald-400 text-xs font-normal leading-4 whitespace-nowrap shrink-0"
                  style={{ fontFamily: "Segoe UI" }}
                >
                  {footer.text}
                </div>
              ) : (
                <div
                  className={`text-xs leading-4 flex-shrink-0 ${footer.variant ? footerColors[footer.variant] : "text-gray-500 dark:text-gray-400"}`}
                  style={{ fontFamily: "Segoe UI" }}
                >
                  {footer.text}
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
